import React, { useState } from 'react';
import {
  Container, Paper, Typography, Button, Box, Grid, Card,
  CardActionArea, Divider, Chip,
  FormControl, Select, MenuItem
} from '@mui/material';
import {
  ArrowForward, ArrowBack, AccountBalance, CheckCircle, Receipt,
  VerifiedUser, Security, Star, FlashOn
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';
import ServiceContactForm from '../components/ServiceContactForm';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const gstServicesList = [
  { id: 'all', name: 'All Services', icon: <FlashOn /> },
  { id: 'gst-reg', name: 'GST Registration', popular: true, docs: ['PAN & Aadhaar', 'Address Proof', 'Bank Account'] },
  { id: 'gst-monthly', name: 'GST Monthly Return (GSTR-1 & 3B)', docs: ['Sales Invoices', 'Purchase Invoices', 'Bank Statement'] },
  { id: 'gst-quarterly', name: 'GST Quarterly (Composition)', docs: ['CMP-08 Turnover Proof', 'Purchase Summary'] },
  { id: 'gst-annual', name: 'GST Annual Return (GSTR-9)', docs: ['Monthly Returns Summary', 'Audited P&L', 'Trial Balance'] },
  { id: 'gst-notice', name: 'GST Legal Notice / SCN Response', popular: true, docs: ['GST Notice Copy', 'ASMT-10 / DRC-01 Form', 'Reconciliation Statement'] },
  { id: 'gst-amend', name: 'GST Amendment', docs: ['New Address Proof', 'Rent Agreement', 'Partner Consent'] },
  { id: 'gst-cancel', name: 'GST Cancellation', docs: ['Final Stock Summary', 'GSTIN Details', 'Reason Letter'] }
];

const planFeatures: Record<string, string[]> = {
  basic: ["Standard filing submission", "GSTR-1 & GSTR-3B processing", "Email support", "Filing acknowledgement"],
  standard: ["GSTR-2A/2B ITC Matching", "Sales & Purchase reconciliation", "Priority CA assistance", "Notice risk check"],
  premium: ["Dedicated GST CA Retainer", "LUT Export Certificate", "E-Way Bill management", "Audit & SCN Legal Response Support"]
};

interface Option { label: string; value: string; subtext?: string; icon: React.ReactNode; }
interface Question { id: number; text: string; description: string; options: Option[]; }

const GstServices: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [recommendedService, setRecommendedService] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'standard' | 'premium'>('standard');

  const questions: Question[] = [
    {
      id: 1,
      text: "What is your primary GST requirement?",
      description: "Select the current GST compliance service your business requires.",
      options: [
        { label: "New GST Registration", value: "registration", subtext: "Fresh GSTIN allotment for new or expanding business", icon: <AccountBalance sx={{ fontSize: 36 }} /> },
        { label: "Regular GST Return Filing", value: "returns", subtext: "Monthly or Quarterly GSTR-1 & GSTR-3B filings", icon: <Receipt sx={{ fontSize: 36 }} /> },
        { label: "Received GST Legal Notice / SCN", value: "notice", subtext: "Departmental show-cause notice response", icon: <Security sx={{ fontSize: 36 }} /> },
        { label: "GST Cancellation or Amendment", value: "cancellation", subtext: "Cancel GSTIN or update address/directors", icon: <VerifiedUser sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 2,
      text: "Are you selling on E-Commerce (Amazon/Flipkart) or Exporting Services?",
      description: "E-Commerce sellers and service exporters require mandatory GST registration and LUT certificates.",
      options: [
        { label: "Yes, E-commerce Seller or Software Exporter", value: "yes_ecom", subtext: "Requires mandatory GST & LUT Filing", icon: <Star sx={{ fontSize: 36 }} /> },
        { label: "No, Local Domestic B2B / B2C Business", value: "no_ecom", subtext: "Standard turnover thresholds apply", icon: <AccountBalance sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 3,
      text: "What is your estimated Annual Business Turnover?",
      description: "Turnover determines composition scheme eligibility and return filing frequency.",
      options: [
        { label: "Below ₹20 Lakhs / ₹40 Lakhs", value: "below_20l", subtext: "Optional or voluntary GST registration", icon: <Receipt sx={{ fontSize: 36 }} /> },
        { label: "₹20 Lakhs to ₹1.5 Crore", value: "20l_1.5cr", subtext: "Eligible for Composition or Regular Scheme", icon: <AccountBalance sx={{ fontSize: 36 }} /> },
        { label: "Above ₹1.5 Crore", value: "above_1.5cr", subtext: "Mandatory Regular Monthly GSTR-1 & 3B", icon: <Star sx={{ fontSize: 36 }} /> }
      ]
    }
  ];

  const handleOptionSelect = (value: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentStep].id]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      const service = calculateRecommendedService();
      setRecommendedService(service);
      setShowRegistration(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateRecommendedService = (): string => {
    const q1 = answers[1];
    const q3 = answers[3];

    if (q1 === 'registration') return 'GST Registration';
    if (q1 === 'notice') return 'GST Notice Response';
    if (q3 === '20l_1.5cr') return 'GST Composition Filing';
    return 'GST Monthly Filing';
  };

  const activeServiceKey = recommendedService || 'GST Registration';

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <SEOHead
        title="GST Registration & Filing Services | Fingard Partners"
        description="Complete GST services — Registration, Monthly & Annual Returns Filing, GST Notice Response, and GST Compliance. Expert CA support from Fingard Partners."
        keywords="GST Registration, GST Filing, GST Returns, GSTR-1, GSTR-3B, GST Notice, GST Services India"
        canonicalUrl="/gst-services"
      />
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 4 }}>
        <Chip label="GST Compliance & Advisory Hub" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
        <Typography variant="h4" fontWeight={800} color="#123D6B" gutterBottom>
          GST Registration, Filing & Notice Portal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
          Complete our 3-step GST diagnostic questionnaire below for an instant service recommendation.
        </Typography>
      </Box>

      {/* DIAGNOSTIC WIZARD SECTION */}
      <Container maxWidth="md">
        {!showRegistration ? (
          <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                Question {currentStep + 1} of {questions.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(((currentStep + 1) / questions.length) * 100)}% Completed
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ mb: 1 }}>
              {questions[currentStep].text}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {questions[currentStep].description}
            </Typography>

            <Grid container spacing={2}>
              {questions[currentStep].options.map((opt, i) => {
                const isSelected = answers[questions[currentStep].id] === opt.value;
                return (
                  <Grid item xs={12} key={i}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: isSelected ? '#123D6B' : '#E2E8F0',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#C59B27', transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardActionArea onClick={() => handleOptionSelect(opt.value)} sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Box sx={{ color: isSelected ? '#123D6B' : '#64748b' }}>
                            {opt.icon}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="#0f172a" sx={{ fontSize: '1.05rem' }}>
                              {opt.label}
                            </Typography>
                            {opt.subtext && (
                              <Typography variant="body2" color="text.secondary">
                                {opt.subtext}
                              </Typography>
                            )}
                          </Box>
                          {isSelected && <CheckCircle color="primary" />}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                disabled={currentStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!answers[questions[currentStep].id]}
                onClick={handleNext}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, bgcolor: '#123D6B', px: 4, '&:hover': { bgcolor: '#C59B27' } }}
              >
                {currentStep === questions.length - 1 ? 'See Recommendation' : 'Next Step'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip label="AI Diagnostic Match" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
                <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                  Recommended Solution: {activeServiceKey}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Based on your diagnostic profile, <strong>{activeServiceKey}</strong> is identified as your required GST solution.
                </Typography>

                {/* SERVICE OVERRIDE DROPDOWN */}
                <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'left', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                    Want a different GST service? Choose from dropdown:
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={activeServiceKey}
                      onChange={(e) => setRecommendedService(e.target.value)}
                      sx={{ borderRadius: 2.5, bgcolor: '#F8FAFC', fontWeight: 600, color: '#123D6B' }}
                    >
                      <MenuItem value="GST Registration">New GST Registration (Fresh Allotment)</MenuItem>
                      <MenuItem value="GST Monthly Filing">Regular GST Return Filing (GSTR-1 & 3B)</MenuItem>
                      <MenuItem value="GST Composition Filing">GST Composition Return (CMP-08)</MenuItem>
                      <MenuItem value="GST Notice Response">GST Legal Notice / SCN Response</MenuItem>
                      <MenuItem value="GST Amendment">GST Address & Director Amendment</MenuItem>
                      <MenuItem value="GST Cancellation">GSTIN Surrender & Cancellation</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Package Tiers Selection (Scope only, no public prices) */}
              <Typography variant="subtitle1" fontWeight="bold" color="#0f172a" sx={{ mb: 1.5 }}>
                Select Service Scope:
              </Typography>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                {(['basic', 'standard', 'premium'] as const).map((plan) => {
                  const isSel = selectedPlan === plan;
                  return (
                    <Grid item xs={12} sm={4} key={plan}>
                      <Card
                        variant="outlined"
                        onClick={() => setSelectedPlan(plan)}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          borderRadius: 3,
                          borderColor: isSel ? '#123D6B' : '#E2E8F0',
                          borderWidth: isSel ? 2 : 1,
                          bgcolor: isSel ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        {plan === 'standard' && (
                          <Chip label="Most Popular" color="secondary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'uppercase', mt: 1 }}>
                          {plan} Package
                        </Typography>
                        <Box sx={{ my: 1.5 }}>
                          <Chip label="Custom CA Quote" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }} />
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        {planFeatures[plan].map((feat, idx) => (
                          <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, textAlign: 'left' }}>
                            ✓ {feat}
                          </Typography>
                        ))}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Service Contact & Quotation Request Form */}
            <ServiceContactForm
              serviceCategory="gst"
              selectedService={activeServiceKey + ' (' + selectedPlan.toUpperCase() + ' Scope)'}
              selectedPackageTier={selectedPlan}
              diagnosticAnswers={answers}
              onBackToQuiz={() => setShowRegistration(false)}
            />
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default GstServices;
